import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getClub } from '../api/clubs';
import { listCatches } from '../api/catches';
import type { CatchResponse, ClubResponse } from '../types/api';
import { ClubHeader } from '../components/clubs/ClubHeader';
import { LeaderboardCard } from '../components/clubs/LeaderboardCard';
import { LatestCatchesList } from '../components/clubs/LatestCatchesList';
import { ClubSessionsList } from '../components/clubs/ClubSessionsList';
import { ContestsSection } from '../components/clubs/ContestsSection';
import { MembersList } from '../components/clubs/MembersList';
import { EditCatchDialog } from '../components/catches/EditCatchDialog';
import { useAuth } from '../contexts/AuthContext';
import { canEditAnyCatch, isReadOnly } from '../lib/permissions';

export function ClubDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [club, setClub] = useState<ClubResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [manageCatches, setManageCatches] = useState(false);
  const [editingCatch, setEditingCatch] = useState<CatchResponse | null>(null);
  const [catchesRefreshKey, setCatchesRefreshKey] = useState(0);

  const clubId = Number(id);
  const isSuperAdmin = user?.isSuperAdmin ?? false;

  useEffect(() => {
    if (isNaN(clubId)) {
      setError('Club not found.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    getClub(clubId)
      .then(setClub)
      .catch(() => setError('Club not found or you are not a member.'))
      .finally(() => setLoading(false));
  }, [clubId]);

  if (loading) return <p className="p-6 text-slate-500 dark:text-slate-400">Loading…</p>;

  if (error || !club) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-6">
        <p className="text-slate-600 dark:text-slate-400">{error ?? 'Club not found.'}</p>
        <Link
          to="/clubs"
          className="text-sm text-teal-600 dark:text-teal-400 hover:underline"
        >
          ← Back to Clubs
        </Link>
      </div>
    );
  }

  const readOnly = isReadOnly(club.role);
  const canManageCatches = canEditAnyCatch(club.role, isSuperAdmin);

  async function handleEditClick(catchId: string) {
    try {
      const res = await listCatches({ clubId, pageSize: 200 });
      const full = res.items.find(c => c.id === catchId);
      if (full) setEditingCatch(full);
    } catch {
      /* swallow; user can retry */
    }
  }

  return (
    <div className="space-y-6">
      <ClubHeader
        clubs={[club]}
        activeClubId={club.id}
        onStartSession={readOnly ? undefined : () => navigate('/sessions/new', { state: { clubId: club.id } })}
      />
      {canManageCatches && (
        <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 shadow-sm">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Manage catches</span>
          <button
            type="button"
            onClick={() => setManageCatches(m => !m)}
            role="switch"
            aria-checked={manageCatches}
            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 ${
              manageCatches ? 'bg-teal-600 dark:bg-teal-500' : 'bg-slate-200 dark:bg-slate-600'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                manageCatches ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LeaderboardCard clubId={club.id} />
        <LatestCatchesList
          clubId={club.id}
          manageMode={manageCatches}
          onEdit={(item) => { void handleEditClick(item.id); }}
          refreshKey={catchesRefreshKey}
        />
      </div>
      <ClubSessionsList clubId={club.id} />
      <ContestsSection clubId={club.id} role={club.role} />
      <MembersList
        clubId={club.id}
        role={club.role}
        isSuperAdmin={isSuperAdmin}
        currentUserId={user?.id ?? ''}
        onLeave={() => navigate('/clubs')}
      />
      <EditCatchDialog
        catchItem={editingCatch}
        open={editingCatch !== null}
        onClose={() => setEditingCatch(null)}
        onSaved={() => {
          setEditingCatch(null);
          setCatchesRefreshKey(k => k + 1);
        }}
        onDeleted={() => {
          setEditingCatch(null);
          setCatchesRefreshKey(k => k + 1);
        }}
      />
    </div>
  );
}
